// Token definitions and source positions.

export interface Pos {
  /** 0-based offset into the source string. */
  offset: number;
  /** 1-based line number. */
  line: number;
  /** 1-based column number. */
  col: number;
}

export interface Span {
  start: Pos;
  end: Pos;
  /** Which source unit these offsets index into. `undefined` means the document
   *  being analyzed (a user program). A non-empty id names a *different* source —
   *  today, a bundled standard-library file (e.g. `"core.stm"`) — so a
   *  definition target in the prelude can be resolved to that file rather than
   *  mis-read as an offset into the user's buffer. See `stdlib.ts`. */
  source?: string;
}

export type TokenType =
  // literals
  | "int"
  | "float"
  | "string"
  | "char"
  | "ident"
  // keywords
  | "struct"
  | "enum"
  | "trait"
  | "impl"
  | "fn"
  | "let"
  | "return"
  | "if"
  | "else"
  | "while"
  | "for"
  | "in"
  | "match"
  | "go"
  | "txn"
  | "retry"
  | "raise"
  | "try"
  | "catch"
  | "assert"
  | "true"
  | "false"
  // punctuation / operators
  | "("
  | ")"
  | "{"
  | "}"
  | "["
  | "]"
  | "<"
  | ">"
  | ","
  | "."
  | ":"
  | ";"
  | "=>"
  | "->"
  | "="
  | "+"
  | "-"
  | "*"
  | "/"
  | "%"
  | "+="
  | "-="
  | "*="
  | "/="
  | "=="
  | "!="
  | "<="
  | ">="
  | "&&"
  | "||"
  | "!"
  | ".."
  | "eof";

export interface Token {
  type: TokenType;
  /** Raw text of the token. */
  text: string;
  /** Parsed value for int/float/string/char literals. A `char` carries its
   *  Unicode code point as a number (like an `int`); a `string` its decoded text. */
  value?: number | string;
  span: Span;
}

export const KEYWORDS: Record<string, TokenType> = {
  struct: "struct",
  enum: "enum",
  trait: "trait",
  impl: "impl",
  fn: "fn",
  let: "let",
  return: "return",
  if: "if",
  else: "else",
  while: "while",
  for: "for",
  in: "in",
  match: "match",
  go: "go",
  txn: "txn",
  retry: "retry",
  raise: "raise",
  try: "try",
  catch: "catch",
  assert: "assert",
  true: "true",
  false: "false",
};
